import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { supabase } from '../lib/supabase'
import { confirmDialog } from '../lib/confirmDialog'
import { useAuth } from '../hooks/useAuth'
import { useTeam } from '../hooks/useTeam'
import Container from '../components/common/Container'
import Card from '../components/common/Card'
import Button from '../components/common/Button'
import Input from '../components/common/Input'
import Select from '../components/common/Select'
import Loading from '../components/common/Loading'
import UpgradePrompt from '../components/common/UpgradePrompt'

export default function RosterManager() {
  
  const navigate = useNavigate()
  const { user, loading: authLoading } = useAuth()
  const { team, loading: teamLoading } = useTeam()
  
  const [roster, setRoster] = useState([])
  const [parents, setParents] = useState([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editingPlayer, setEditingPlayer] = useState(null)
  const [formData, setFormData] = useState({
    player_name: '',
    jersey_number: '',
    position: '',
    parent_id: ''
  })

  // Redirect if not logged in or no team
  useEffect(() => {
    
    if (!authLoading && !teamLoading) {
      if (!user) {
        navigate('/auth')
      } else if (!team) {
        navigate('/team-setup')
      }
    }
  }, [user, team, authLoading, teamLoading, navigate])

  // Fetch roster and parents
  useEffect(() => {
    if (team?.id) {
      fetchRoster()
      fetchParents()
    }
  }, [team?.id])

  const fetchRoster = async () => {
    setLoading(true)
    
    try {
      const { data, error } = await supabase
        .from('roster')
        .select('*, parents(email)')
        .eq('team_id', team.id)
        .order('player_name')

      if (error) throw error

      setRoster(data || [])
    } catch (err) {
      alert('Failed to load roster')
    } finally {
      setLoading(false)
    }
  }

  const fetchParents = async () => {
    
    try {
      const { data, error } = await supabase
        .from('parents')
        .select('id, email')
        .eq('team_id', team.id)
        .order('email')

      if (error) throw error

      setParents(data || [])
    } catch (err) {
    }
  }

  const handleOpenForm = (player = null) => {
    
    if (player) {
      setEditingPlayer(player)
      setFormData({
        player_name: player.player_name,
        jersey_number: player.jersey_number || '',
        position: player.position || '',
        parent_id: player.parent_id || ''
      })
    } else {
      setEditingPlayer(null)
      setFormData({
        player_name: '',
        jersey_number: '',
        position: '',
        parent_id: ''
      })
    }
    
    setShowForm(true)
  }

  const handleCloseForm = () => {
    setShowForm(false)
    setEditingPlayer(null)
    setFormData({
      player_name: '',
      jersey_number: '',
      position: '',
      parent_id: ''
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!formData.player_name.trim()) {
      alert('Player name is required')
      return
    }

    try {
      if (editingPlayer) {
        // Update existing player
        const { error } = await supabase
          .from('roster')
          .update({
            player_name: formData.player_name.trim(),
            jersey_number: formData.jersey_number.trim() || null,
            position: formData.position.trim() || null,
            parent_id: formData.parent_id || null
          })
          .eq('id', editingPlayer.id)
          .eq('team_id', team.id)

        if (error) throw error

      } else {
        // Create new player
        const { error } = await supabase
          .from('roster')
          .insert({
            team_id: team.id,
            player_name: formData.player_name.trim(),
            jersey_number: formData.jersey_number.trim() || null,
            position: formData.position.trim() || null,
            parent_id: formData.parent_id || null
          })

        if (error) throw error

      }

      handleCloseForm()
      fetchRoster()
    } catch (err) {
      toast.error('Failed to save player')
    }
  }

  const handleDelete = async (playerId) => {
    const confirmed = await confirmDialog(
      'Remove this player from the roster?',
      { confirmText: 'Remove', cancelText: 'Cancel' }
    )
    if (!confirmed) return

    try {
      const { error } = await supabase
        .from('roster')
        .delete()
        .eq('id', playerId)
        .eq('team_id', team.id)

      if (error) throw error

      fetchRoster()
    } catch (err) {
      alert('Failed to delete player')
    }
  }

  // Utility function to sanitize player name for public display
  const sanitizeName = (fullName) => {
    const parts = fullName.trim().split(' ')
    if (parts.length === 1) {
      return parts[0] // Single name, return as is
    }
    const firstName = parts[0]
    const lastInitial = parts[parts.length - 1][0]
    return `${firstName} ${lastInitial}.`
  }

  if (authLoading || teamLoading || !team) {
    return <Loading />
  }

  // Check if team has paid tier
  if (team.season_tier !== 'paid') {
    return (
      <Container>
        <div className="max-w-4xl mx-auto py-8">
          <div className="mb-8">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/dashboard')}
              className="mb-4"
            >
              ← Back to Dashboard
            </Button>
            <h1 className="text-3xl font-bold text-brand-green">Team Roster</h1>
          </div>
          
          <UpgradePrompt
            title="Roster Management is a Pro Feature"
            features={[
              'Add unlimited players',
              'Track jersey numbers and positions',
              'Link players to parent contacts',
              'Public portal shows privacy-safe names (First + Last Initial)',
              'Full roster management dashboard'
            ]}
            tier="paid"
          />
        </div>
      </Container>
    )
  }

  return (
    <Container>
      <div className="max-w-4xl mx-auto py-8">
        {/* Header */}
        <div className="mb-8">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/dashboard')}
            className="mb-4"
          >
            ← Back to Dashboard
          </Button>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-brand-green">Team Roster</h1>
              <p className="text-gray-600 mt-2">
                Manage your team roster. Public portal will show "First L." format for privacy.
              </p>
            </div>
            <Button onClick={() => handleOpenForm()}>
              + Add Player
            </Button>
          </div>
        </div>

        {/* Privacy Notice */}
        <Card className="mb-6 bg-blue-50 border-blue-200">
          <div className="flex items-start gap-3">
            <span className="text-2xl">🔒</span>
            <div>
              <h3 className="font-semibold text-gray-900 mb-1">
                Youth Privacy Protection
              </h3>
              <p className="text-sm text-gray-700">
                Full names are stored securely. The public portal will display names as "First L." (e.g., "John D.") 
                to protect player privacy. You can control roster visibility in Portal Settings.
              </p>
            </div>
          </div>
        </Card>

        {/* Roster List */}
        {loading ? (
          <Loading />
        ) : roster.length === 0 ? (
          <Card>
            <div className="text-center py-12">
              <span className="text-6xl mb-4 block">👥</span>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                No Players Yet
              </h3>
              <p className="text-gray-600 mb-6">
                Add your first player to start building your roster.
              </p>
              <Button onClick={() => handleOpenForm()}>
                + Add First Player
              </Button>
            </div>
          </Card>
        ) : (
          <div className="space-y-3">
            {roster.map((player) => (
              <Card key={player.id} className="hover:shadow-md transition-shadow">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-900 break-words">
                          {player.player_name}
                        </h3>
                        <p className="text-sm text-gray-500">
                          Public displays as: <span className="font-medium text-brand-green">{sanitizeName(player.player_name)}</span>
                        </p>
                      </div>
                      
                      <div className="flex items-center gap-3 flex-wrap">
                        {player.jersey_number && (
                          <div className="flex items-center gap-2 text-sm">
                            <span className="text-gray-500">#</span>
                            <span className="font-semibold text-brand-green">
                              {player.jersey_number}
                            </span>
                          </div>
                        )}
                        
                        {player.position && (
                          <div className="px-3 py-1 bg-gray-100 rounded text-sm text-gray-700 whitespace-nowrap">
                            {player.position}
                          </div>
                        )}
                        
                        {player.parents?.email && (
                          <div className="text-sm text-gray-600 break-all">
                            📧 {player.parents.email}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleOpenForm(player)}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(player.id)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      Remove
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Add/Edit Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <Card className="max-w-md w-full max-h-[90vh] overflow-y-auto">
              <h2 className="text-2xl font-bold text-brand-green mb-6">
                {editingPlayer ? 'Edit Player' : 'Add Player'}
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Player Name <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="text"
                    value={formData.player_name}
                    onChange={(e) => setFormData({ ...formData, player_name: e.target.value })}
                    placeholder="John Doe"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Will display publicly as: {formData.player_name ? sanitizeName(formData.player_name) : 'First L.'}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Jersey Number
                  </label>
                  <Input
                    type="text"
                    value={formData.jersey_number}
                    onChange={(e) => setFormData({ ...formData, jersey_number: e.target.value })}
                    placeholder="12"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Position
                  </label>
                  <Input
                    type="text"
                    value={formData.position}
                    onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                    placeholder="Forward, Midfielder, etc."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Link to Parent (Optional)
                  </label>
                  <Select
                    value={formData.parent_id}
                    onChange={(e) => setFormData({ ...formData, parent_id: e.target.value })}
                  >
                    <option value="">No parent linked</option>
                    {parents.map((parent) => (
                      <option key={parent.id} value={parent.id}>
                        {parent.email}
                      </option>
                    ))}
                  </Select>
                  <p className="text-xs text-gray-500 mt-1">
                    Link this player to a parent contact from your parent list
                  </p>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button type="submit" className="flex-1">
                    {editingPlayer ? 'Save Changes' : 'Add Player'}
                  </Button>
                  <Button 
                    type="button" 
                    variant="ghost" 
                    onClick={handleCloseForm}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </Card>
          </div>
        )}
      </div>
    </Container>
  )
}

